import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import settingService from "api/settingsService";
import classNames from "classnames";
import Spinner from "components/Spinner";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import SettingsIcon from "assets/icons/settings_24px.svg?react";
import { Tooltip } from "@mui/material";
import BackIcon from "assets/icons/back.svg?react";
import EmailIcon from "assets/icons/email.svg?react";
import ArrowLeftIcon from "assets/icons/arrow_l.svg?react";
import { useOnClickOutside } from "hooks/useClickOutside";
import SearchMemberInWorkspace from "components/SearchMemberInWorkspace";
import Chip from "components/Chip";
import { Avatar } from "@mui/material";
import { convertToLetters } from "utils/convertToLetters";
import { useAuth } from "react-oidc-context";
import { IntegrationType } from "dto/Settings/IntegrationType";
import { IExternalIntegration } from "dto/Settings/IIntegrationsResponse";
import Input from "components/Input";
import RichEditor from "components/RichEditor";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import workspaceService from "api/workspaceService";
import { IWorkspace, IWorkspaceMember } from "dto/IWorkspace";
import { APPLICATIONS } from "../IntegrationMenu";
import { ETypes } from "../IntegrationMenu/typings";
import { IntegrationProvider } from "dto/Settings/IntegrationProvider";
import ConfigureIMAP from "components/ConfigureIMAP";
import { reEncrypt } from "utils/reEncrypt";
import { getUserId } from "utils/getUserId";
import { randomString } from "utils/randomString";
import { readAsBase64 } from "utils/readAsBase64";
import { ENotificationType, notification } from "utils/notification";
import { getErrorMessage } from "utils/getErrorMessage";
import OauthPopup from "components/OauthPopup";
import { useTranslation } from "react-i18next";

const EmailTab: FC<Props> = ({ showTabBar }) => {
  const { t } = useTranslation();
  const availableIntegrations = APPLICATIONS.filter(
    (x) => x.type === ETypes.EMAIL,
  );

  const {
    data: integrationsData,
    isLoading,
    refetch: refetchI,
  } = settingService.useGetIntegrationSettings();

  const { mutate: configureIntegration } =
    settingService.useConfigureIntegration();

  const [workspaceId] = useState(getWorkspaceId() ?? "");

  const { data: workspaceData } =
    workspaceService.useGetWorkspace(workspaceId);

  const workspace: IWorkspace = useMemo(
    () => workspaceData?.data as IWorkspace,
    [workspaceData],
  );

  const workspaceMembers = useMemo(() => workspace?.Members ?? [], [workspace]);


  const { mutate: getMsRedirect } = settingService.useGetMsredirect();

  const [selectedItem, setSelectedItem] = useState<IExternalIntegration>(null);

  const { data: aliasesData } = settingService.useGetAliases(
    selectedItem?.Id,
    !selectedItem,
  );

  const { data: accessData } = settingService.useGetUserAccesses(
    selectedItem?.Id,
    !selectedItem,
  );

  const { mutate: addEmailAlias } = settingService.useAddEmailAlias();
  const { mutate: removeEmailAlias } = settingService.useRemoveEmailAlias();

  const { mutate: createAccess } = settingService.useCreateIntegrationAccess();
  const { mutate: removeAccess } = settingService.useRemoveIntegrationAccess();

  const { mutate: updateFooter, isLoading: isSavingFooter } =
    settingService.useSetFooter();
  const { data: footerData, isLoading: isFooterLoading } =
    settingService.useGetFooter();

  const emails = useMemo(
    () =>
      integrationsData?.data?.Active.filter(
        (x) => x.Type === IntegrationType.Email,
      ),
    [integrationsData],
  );

  const auth = useAuth();
  const userId = getUserId(auth.user!);

  const [showCreate, setShowCreate] = useState<boolean>(false);
  const [showModify, setShowModify] = useState<boolean>(false);

  const [admins, setAdmins] = useState<IWorkspaceMember[]>([]);

  const dropdownCreateRef = useRef();

  const [aliasValue, setAliasValue] = useState<string>("");
  const [aliases, setAliases] = useState<string[]>([]);

  const [content, setContent] = useState("");
  const editorRef = useRef<any>(null);
  const [confImap, setConfImap] = useState(false);

  useEffect(() => {
    if (footerData) {
      setContent(footerData?.data?.Content ?? "");
    }
  }, [footerData]);

  useEffect(() => {
    if (workspaceMembers && accessData) {
      setAdmins(
        workspaceMembers.filter(
          (x) => x.UserId && accessData.data.indexOf(x.UserId) !== -1,
        ),
      );
    }
  }, [workspaceMembers, accessData]);

  useOnClickOutside(dropdownCreateRef, () => {
    setShowCreate(false);
  });

  const changeAliasChange = (v: string) => {
    if (v.endsWith(" ")) {
      const a = v.trim();

      addEmailAlias(
        {
          Id: selectedItem?.Id,
          Alias: a,
        },
        {
          onSuccess() {
            setAliasValue("");
            setAliases([...aliases, v]);
          },
        },
      );
    } else {
      setAliasValue(v);
    }
  };

  const handleRemoveAlias = (v: string) => {
    removeEmailAlias(
      {
        Id: selectedItem?.Id,
        Alias: v,
      },
      {
        onSuccess() {
          setAliases([...aliases.filter((x) => x !== v)]);
        },
      },
    );
  };

  const handleSaveFooter = () => {
    updateFooter({
      Content: content,
    });
  };


  const handleAddUser = async (member: IWorkspaceMember) => {
    // wygenerowanie klucza
    const uint = await reEncrypt(userId, member.UserId, {
      Id: randomString(),
      PrivateKey: selectedItem.PrivateKey,
      PublicKey: null,
      ParentKeyId: null,
      Initial: false,
      Armored: false,
      EmptyPassphrase: false,
    });
    const sharedKey = await readAsBase64(uint);

    createAccess(
      {
        Id: selectedItem.Id,
        UserId: member.UserId,
        PrivateKey: sharedKey,
      },
      {
        onSuccess() {
          setAdmins([...admins, member]);
        },
        onError(error) {
          notification({
            type: ENotificationType.ERROR,
            title: t("settings.email.addUserFailed"),
            text: getErrorMessage(error),
          });
        },
      },
    );
  };
  const handleRemoveAccess = (userId: string) => {
    removeAccess(
      {
        Id: selectedItem.Id,
        UserId: userId,
      },
      {
        onSuccess() {
          setAdmins([...admins.filter((x) => x.UserId !== userId)]);
        },
      },
    );
  };

  const handleConfigureIntegration = (application) => {
    if (application.type === ETypes.EMAIL) {
      if (application.provider === IntegrationProvider.Google) {
        //loginGoogleEmail();
        return;
      }
      if (application.provider === IntegrationProvider.Microsoft) {
        initMicrosoft();
        return;
      }
      if (application.provider === IntegrationProvider.Imap) {
        setConfImap(true);
        return;
      }
    }
  };

  const [msRedirecUrl, setMsRedirecUrl] = useState("");
  const scopes = [
    "user.read",
    "mail.readwrite",
    "mail.send",
    "contacts.readwrite",
  ] as string[];

  const initMicrosoft = () => {
    getMsRedirect(
      { scopes: scopes.join(", ") },
      {
        onSuccess(data) {
          const url = data.data.Url;
          setMsRedirecUrl(url);
        },
      },
    );
  };

  const onCode = (code: any, params: any) => {
    configureIntegration(
      {
        Provider: IntegrationProvider.Microsoft,
        Type: IntegrationType.Email,
        Code: code,
        Scope: scopes!.join(", "),
      },
      {
        onSuccess() {
          refetchI();
          setShowCreate(false);
        },
        onError(e: any) {
          notification({
            title: t("integrations.configureFailed"),
            text: getErrorMessage(e.response.data),
            type: ENotificationType.ERROR,
          });
          setMsRedirecUrl(null);
        },
      },
    );
  };

  useEffect(() => {
    if (aliasesData?.data) {
      setAliases(aliasesData.data);
    }
  }, [aliasesData]);

  useEffect(() => {
    if (emails?.length) {
      setShowCreate(false);
    } else {
      setShowCreate(true);
    }
  }, [emails]);

  if (isLoading) {
    return <Spinner></Spinner>;
  }

  if (confImap) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.pathBar}>
          <div className={styles.backButton}>
            <BackIcon onClick={() => setConfImap(false)}></BackIcon>
          </div>
          Email <ArrowLeftIcon></ArrowLeftIcon> {t("settings.email.imapHeader")}
        </div>
        <ConfigureIMAP
          onSuccess={() => {
            refetchI();
            setConfImap(false);
            setShowCreate(false);
          }}
          toggle={() => setConfImap(false)}
        />
      </div>
    );
  }

  if (showCreate) {
    return (
      <>
        <OauthPopup
          width={400}
          height={600}
          title={t("common.signIn")}
          url={msRedirecUrl}
          onClose={() => {}}
          onCode={onCode}
        >
          <div></div>
        </OauthPopup>
        <div className={styles.wrapper}>
          {!!emails?.length && (
            <div className={styles.pathBar}>
              <div className={styles.backButton}>
                <BackIcon onClick={() => setShowCreate(false)}></BackIcon>
              </div>
              Email <ArrowLeftIcon></ArrowLeftIcon> {t("settings.addNewSync")}
            </div>
          )}

          {!emails?.length && (
            <div>
              <div className={styles.title}>
                <EmailIcon></EmailIcon>Email
              </div>
              <div className={styles.hint}>
                {t("settings.email.hint")}
              </div>
            </div>
          )}

          <div className={styles.applications}>
            {availableIntegrations.map((a, index) => (
              <div
                onClick={() => handleConfigureIntegration(a)}
                className={styles.application}
                key={"appInteg" + index}
              >
                <div className={styles.appIcon}>{a.image}</div>
                <div className={styles.appTitle}>{a.name}</div>
                <div className={styles.appDescription}>{t(a.description)}</div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className={styles.wrapper}>
      {!selectedItem && (
        <>
          <div className={styles.title}>
            <EmailIcon></EmailIcon>Email
          </div>
          <div className={styles.hint}>
            {t("settings.email.hint")}
          </div>
          <div className={styles.group}>
            <div className={styles.list}>
              {emails.map((email) => (
                <div key={"email_" + email.Id} className={`${styles.listItem}`}>
                  <div className={styles.left}>
                    <div className={styles.label}>{email.Name}</div>
                    <div className={styles.chips}>
                      <div className={classNames(styles.status, styles.active)}>
                        {t("integrations.syncActive")}
                      </div>
                      {email.IsShared && (
                        <Tooltip title={`Administrator: ${email.OwnerLabel}`}>
                          <div
                            className={classNames(styles.status, styles.shared)}
                          >
                            {t("settings.email.shared")}
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  {!email.ReadOnly && (
                    <div className={styles.right}>
                      <div className={styles.settings}>
                        <Tooltip title={t("settings.email.settingsTooltip")}>
                          <div
                            onClick={() => {
                              setSelectedItem(email);
                            }}
                          >
                            <SettingsIcon></SettingsIcon>
                          </div>
                        </Tooltip>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className={styles.btnWrapper}>
              <Button
                className={styles.btn}
                onClick={() => {
                  setShowCreate(!showCreate);
                }}
                text={t("settings.addNewSync")}
              />
            </div>
          </div>
        </>
      )}

      {selectedItem && (
        <>
          <div className={styles.group}>
            <div className={styles.pathBar}>
              <div className={styles.backButton}>
                <BackIcon onClick={() => setSelectedItem(null)}></BackIcon>
              </div>
              Email <ArrowLeftIcon></ArrowLeftIcon> {t("settings.email.mailboxConfigHeader")}
            </div>

            <div className={styles.list}>
              <div className={`${styles.listItem}`}>
                <div className={styles.left}>
                  <div className={styles.label}>{selectedItem.Name}</div>
                  <div className={classNames(styles.status, styles.active)}>
                    {t("integrations.syncActive")}
                  </div>
                </div>
                <div className={styles.right}></div>
              </div>
            </div>

            <div className={styles.buttons}>
              <HollowButton
                className={styles.btn}
                onClick={() => {
                  setShowModify(!showModify);
                }}
                text={t("settings.disconnectIntegration")}
              />

              <Button onClick={() => {}} text={t("common.refresh")}></Button>
            </div>
          </div>

          <div className={styles.group}>
            <div className={styles.header}>{t("settings.email.mailboxAccess")}</div>
            <div className={styles.hint}>
              {t("settings.email.mailboxAccessHint")}
            </div>
            <SearchMemberInWorkspace
              onChoose={(member) => {
                handleAddUser(member);
              }}
              clearAfterChoose={true}
              label={t("settings.email.findUser")}
              showMe={false}
            />
            <div className={styles.members}>
              {admins.map((member) => (
                <Chip
                  key={member.UserId}
                  avatar={
                    <Avatar className={styles.avatar}>
                      {convertToLetters(member.Label)}
                    </Avatar>
                  }
                  element={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      {member.Label}
                    </div>
                  }
                  onDelete={() => handleRemoveAccess(member.UserId)}
                />
              ))}
            </div>

            <div className={styles.header}>{t("settings.email.emailAliases")}</div>
            <div className={styles.hint}>
              {t("settings.email.aliasesHint")}
            </div>
            <Input
              label=""
              labelPosition="none"
              type="text"
              value={aliasValue}
              onChange={changeAliasChange}
              placeholder={t("settings.email.aliasPlaceholder")}
            ></Input>
            <div className={styles.hint2}>
              {t("settings.email.aliasFormat")}
            </div>
            <div className={styles.members}>
              {aliases.map((a, i) => (
                <Chip
                  key={"alias" + i}
                  element={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                    >
                      {a}
                    </div>
                  }
                  onDelete={() => handleRemoveAlias(a)}
                />
              ))}
            </div>

            <div className={styles.header}>{t("settings.email.footerConfig")}</div>
            <div className={styles.hint}>
              {t("settings.email.footerHint")}
            </div>
            <div className={classNames(styles.editor)}>
              {!isFooterLoading && (
                <RichEditor
                  setContent={setContent}
                  ref={editorRef}
                  content={content}
                  allowShowCode={true}
                />
              )}
            </div>
            <div className={styles.buttons}>
              <Button
                text={t("common.save")}
                onClick={handleSaveFooter}
                loading={isSavingFooter}
              ></Button>
            </div>
          </div>

        </>
      )}
    </div>
  );
};

export default EmailTab;
